import { useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash, UploadSimple, FloppyDisk } from "@phosphor-icons/react";

const emptyExercise = { name: "", sets: 3, reps: "10", rest_seconds: 60, video_url: "", notes: "" };

export default function PlanBuilder({ clientId, initialPlan }) {
  const [title, setTitle] = useState(initialPlan?.title || "");
  const [days, setDays] = useState(initialPlan?.days?.length ? initialPlan.days : [{ day_name: "Day 1", exercises: [{ ...emptyExercise }] }]);
  const [pdfFileId, setPdfFileId] = useState(initialPlan?.pdf_file_id || null);
  const [pdfFilename, setPdfFilename] = useState(initialPlan?.pdf_filename || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const addDay = () => setDays((d) => [...d, { day_name: `Day ${d.length + 1}`, exercises: [{ ...emptyExercise }] }]);
  const removeDay = (i) => setDays((d) => d.filter((_, idx) => idx !== i));
  const updateDay = (i, key, val) => setDays((d) => d.map((day, idx) => (idx === i ? { ...day, [key]: val } : day)));
  const addExercise = (i) => setDays((d) => d.map((day, idx) => (idx === i ? { ...day, exercises: [...day.exercises, { ...emptyExercise }] } : day)));
  const removeExercise = (i, ei) =>
    setDays((d) => d.map((day, idx) => (idx === i ? { ...day, exercises: day.exercises.filter((_, eidx) => eidx !== ei) } : day)));
  const updateExercise = (i, ei, key, val) =>
    setDays((d) =>
      d.map((day, idx) => (idx === i ? { ...day, exercises: day.exercises.map((ex, eidx) => (eidx === ei ? { ...ex, [key]: val } : ex)) } : day))
    );

  const uploadPdf = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setPdfFileId(data.file_id);
      setPdfFilename(data.filename);
      toast.success("PDF uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/clients/${clientId}/plan`, { title, days, pdf_file_id: pdfFileId, pdf_filename: pdfFilename });
      toast.success("Plan saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save plan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="plan-builder" className="mt-4 space-y-6">
      <input
        data-testid="plan-title-input"
        placeholder="Plan title (e.g. Functional Strength - Phase 1)"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white"
      />

      <div className="border border-[#27272A] rounded-xl p-4 flex items-center justify-between">
        <div>
          <p className="text-sm text-white">1-Click PDF Upload</p>
          <p className="text-xs text-[#71717A]">{pdfFilename || "No file attached"}</p>
        </div>
        <label data-testid="plan-pdf-upload-label" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-xs px-4 py-2 rounded-full cursor-pointer transition-colors">
          <UploadSimple size={14} /> {uploading ? "Uploading..." : "Upload PDF"}
          <input data-testid="plan-pdf-upload-input" type="file" accept="application/pdf" className="hidden" onChange={uploadPdf} />
        </label>
      </div>

      <div data-testid="plan-helper-tip-banner" className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl px-4 py-3 text-sm text-[#D4AF37]">
        💡 Tip: Add workout days, enter exercise details (Sets x Reps, Rest time in seconds), and optional YouTube video links for client guidance.
      </div>

      {days.map((day, i) => (
        <div key={i} data-testid={`plan-day-${i}`} className="border border-[#27272A] rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-3">
            <input
              data-testid={`plan-day-name-input-${i}`}
              value={day.day_name}
              onChange={(e) => updateDay(i, "day_name", e.target.value)}
              className="flex-1 bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-1.5 text-white text-sm"
            />
            <button data-testid={`plan-remove-day-${i}`} onClick={() => removeDay(i)} className="text-[#71717A] hover:text-red-400">
              <Trash size={16} />
            </button>
          </div>
          <div className="grid grid-cols-12 gap-2 px-0.5">
            <span className="col-span-3 text-[10px] uppercase tracking-wide text-[#71717A]">Exercise Name</span>
            <span className="col-span-2 text-[10px] uppercase tracking-wide text-[#71717A]">Sets</span>
            <span className="col-span-2 text-[10px] uppercase tracking-wide text-[#71717A]">Reps</span>
            <span className="col-span-2 text-[10px] uppercase tracking-wide text-[#71717A]">Rest / Duration (sec)</span>
            <span className="col-span-2 text-[10px] uppercase tracking-wide text-[#71717A]">YouTube Demo URL</span>
          </div>
          {day.exercises.map((ex, ei) => (
            <div key={ei} data-testid={`plan-exercise-${i}-${ei}`} className="grid grid-cols-12 gap-2 items-center">
              <input
                data-testid={`exercise-name-input-${i}-${ei}`}
                placeholder="e.g. Barbell Squat"
                value={ex.name}
                onChange={(e) => updateExercise(i, ei, "name", e.target.value)}
                className="col-span-3 bg-[#0A0A0A] border border-[#27272A] rounded-lg px-2 py-1.5 text-white text-xs"
              />
              <input
                data-testid={`exercise-sets-input-${i}-${ei}`}
                type="number"
                placeholder="3"
                value={ex.sets}
                onChange={(e) => updateExercise(i, ei, "sets", parseInt(e.target.value) || 0)}
                className="col-span-2 bg-[#0A0A0A] border border-[#27272A] rounded-lg px-2 py-1.5 text-white text-xs"
              />
              <input
                data-testid={`exercise-reps-input-${i}-${ei}`}
                placeholder="e.g. 10 or 8-12"
                value={ex.reps}
                onChange={(e) => updateExercise(i, ei, "reps", e.target.value)}
                className="col-span-2 bg-[#0A0A0A] border border-[#27272A] rounded-lg px-2 py-1.5 text-white text-xs"
              />
              <input
                data-testid={`exercise-rest-input-${i}-${ei}`}
                type="number"
                placeholder="60"
                value={ex.rest_seconds}
                onChange={(e) => updateExercise(i, ei, "rest_seconds", parseInt(e.target.value) || 0)}
                className="col-span-2 bg-[#0A0A0A] border border-[#27272A] rounded-lg px-2 py-1.5 text-white text-xs"
              />
              <input
                data-testid={`exercise-video-input-${i}-${ei}`}
                placeholder="Optional: https://youtube.com/..."
                value={ex.video_url || ""}
                onChange={(e) => updateExercise(i, ei, "video_url", e.target.value)}
                className="col-span-2 bg-[#0A0A0A] border border-[#27272A] rounded-lg px-2 py-1.5 text-white text-xs"
              />
              <button
                data-testid={`exercise-remove-button-${i}-${ei}`}
                onClick={() => removeExercise(i, ei)}
                className="col-span-1 text-[#71717A] hover:text-red-400 justify-self-end"
              >
                <Trash size={14} />
              </button>
            </div>
          ))}
          <button data-testid={`plan-add-exercise-${i}`} onClick={() => addExercise(i)} className="flex items-center gap-1 text-xs text-[#D4AF37]">
            <Plus size={14} /> Add Exercise
          </button>
        </div>
      ))}
      <div className="flex items-center justify-between">
        <button data-testid="plan-add-day-button" onClick={addDay} className="flex items-center gap-2 text-sm text-[#D4AF37]">
          <Plus size={16} /> Add Day
        </button>
        <button
          data-testid="plan-save-button"
          onClick={save}
          disabled={saving || !title}
          className="flex items-center gap-2 bg-[#D4AF37] disabled:opacity-40 text-black font-semibold px-6 py-2.5 rounded-full"
        >
          <FloppyDisk size={16} /> {saving ? "Saving..." : "Save Plan"}
        </button>
      </div>
    </div>
  );
}
